import axiosInstance from 'api/axiosInstance';
import type { AxiosRequestConfig } from 'axios';
import type {
  AIAgentEnum,
  ConversationHistory,
  ConversationMessagePart,
  ConversationMetadata,
  ConversationMetadataType,
  ConversationQuery,
  SuggestedQuestion,
  SuggestedQuestionUseCaseEnum,
} from 'types/ai.types';

// constant to track allowed tools and features for AI Tax Advisor
const AITA_VERSION = '1.0.2';

export const getConversationUsage = (): Promise<{
  monthly_message_count: number;
  monthly_message_limit: number;
  conversation_message_limit: number;
}> => axiosInstance.get('/v2/conversations/messages/monthly-usage');

export const createConversation = (
  body: ConversationQuery,
  config: AxiosRequestConfig,
): Promise<ConversationHistory> =>
  axiosInstance
    .post(
      '/v3/conversations/stream',
      { ...body, aita_version: AITA_VERSION },
      config,
    )
    .then(({ data }) => ({ ...data, uid: new Date() }));

export const getConversations = (params: {
  page: number;
  perPage: number;
  source?: ConversationMetadataType;
  ai_agent?: AIAgentEnum;
}): Promise<{
  conversations: ConversationHistory[];
  paging: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}> => {
  return axiosInstance.get('/v3/conversations', { params });
};

export const updateConversation = (
  { id, ...body }: ConversationQuery,
  config: AxiosRequestConfig,
): Promise<ConversationHistory> =>
  axiosInstance
    .post(
      `/v3/conversations/${id}/messages/stream`,
      { ...body, aita_version: AITA_VERSION },
      config,
    )
    .then(({ data }) => ({ ...data, uid: new Date() }));

export const getConversationMessages = (
  id?: string,
): Promise<ConversationHistory> =>
  axiosInstance
    .get(`/v2/conversations/${id}`)
    .then(({ data }) => ({ ...data, uid: new Date() }));

export const patchConversation = (
  conversationId: string,
  body: Pick<ConversationHistory, 'rating' | 'intercomShareConversation'>,
): Promise<ConversationHistory> =>
  axiosInstance
    .patch(`/v3/conversations/${conversationId}`, {
      ...body,
      aita_version: AITA_VERSION,
    })
    .then(({ data }) => data);

export const deleteConversation = (
  id: string,
): Promise<ConversationHistory | undefined> =>
  axiosInstance.delete(`/v2/conversations/${id}`);

export const talkToCoach = (
  conversationId: string,
  messageId: string,
): Promise<any> => {
  return axiosInstance.put(
    `/v2/conversations/${conversationId}/messages/${messageId}/intercom-cta`,
  );
};

export type PatchConversationMessageBody = {
  intercomCTA?: boolean;
  allowed_data_access_on_form?: boolean; // TODO: can we remove this?
  parts?: ConversationMessagePart[];
};

export const patchConversationMessage = (
  conversationId: string,
  messageId: string,
  body: PatchConversationMessageBody,
): Promise<any> => {
  return axiosInstance.patch(
    `/v3/conversations/${conversationId}/messages/${messageId}`,
    { ...body, aita_version: AITA_VERSION },
  );
};

export const regenerateConversationMessage = (
  conversationId: string,
  messageId: string,
  config: AxiosRequestConfig,
): Promise<any> => {
  return axiosInstance.post(
    `/v3/conversations/${conversationId}/messages/${messageId}/stream`,
    { aita_version: AITA_VERSION },
    config,
  );
};

export const getConversationMetadata = (
  conversationId: string,
): Promise<{ data: ConversationMetadata[] }> =>
  axiosInstance.get(`/v2/conversations/${conversationId}/metadata`);

export const postConversationMetadata = (
  metadata: ConversationMetadata,
): Promise<ConversationMetadata> =>
  axiosInstance.post(`/v2/conversations/metadata`, metadata);

export const searchExpenseCategories = (
  text: string,
  lang = 'en',
): Promise<any> =>
  axiosInstance.get('/v2/expenses/categories/search', {
    params: { text, lang },
  });

export const predictExpenseCategory = (text: string): Promise<any> =>
  axiosInstance.get('/v2/expenses/categories/predict', { params: { text } });

export const getSuggestedQuestions = (
  use_case: SuggestedQuestionUseCaseEnum,
): Promise<{ data: SuggestedQuestion[] }> =>
  axiosInstance.get('/v2/conversations/suggested-prompts', {
    params: { use_case, aita_version: AITA_VERSION },
  });

export const postSuggestedQuestions = (
  promptId: string,
  order: number,
  prompt_metadata?: Record<string, any>,
): Promise<any> =>
  axiosInstance.post('/v2/conversations/suggested-prompts', {
    promptId,
    order,
    prompt_metadata,
  });
